// eslint-disable-next-line import/no-unresolved
import $ from 'jquery';

import { sprintf } from '@wordpress/i18n';

import {
	itemsObject,
	getGlobalItem,
	parseStringWithTokens,
	addUTMParameters,
} from '../common/utilities.js';

import { getIntegrationIconURL } from '@builder/integrations/utilities';

import { getActions, getActionsThatRunBefore } from '@builder/item/actions/utilities';

// Import utilities
const {
	isDefined,
	isEmpty,
	isObject,
	supportsRegExLookbehind
} = _uo.utility;

import Modal from '../common/modal';

import Field from '../field';

/**
 * Parses tokens in strings
 */
export default class Tokens {
	/**
	 * Saves useful data in the instance, and creates all the tokens for a given field
	 *
	 * @param {String} itemType  The item type (trigger/action/user-selector)
	 * @param {String} fieldType The field type
	 * @param {Object} options   Optional options
	 * @return {undefined}
	 */
	constructor( itemType, fieldType = 'text', options = {} ) {
		// Save data
		this.saveData( itemType, fieldType, options );

		// Get data
		this.getData();
	}

	/**
	 * Saves useful data in the token instance
	 *
	 * @param {String} itemType  The item type (trigger/action/user-selector)
	 * @param {String} fieldType The field type
	 * @param {Object} options   Optional options
	 * @return {undefined}
	 */
	saveData( itemType, fieldType, options ) {
		this.itemType = itemType;
		this.fieldType = fieldType;

		this.options = Object.assign(
			{
				includeGroupName: true,
				includeGroupIcon: true,
				includeGroupTokens: true,
				unsupportedTokens: [],
			},
			options
		);

		this.tokensRegEx = new RegExp( /\{\{(.*?)\}\}/gm );

		if ( supportsRegExLookbehind() ) {
			// Exclude tokens inside HTML attributes
			// <a href="{{a}}">{{b}}</a> will return only {{b}}
			// Uses lookbehind https://caniuse.com/js-regexp-lookbehind
			this.tokensRegExNoAttributes = new RegExp( '(?<=[^\="])\{\{(.*?)\}\}(?<=[^\"])', 'gm' );
		} else {
			// Fallback for basic browsers, like Safari
			this.tokensRegExNoAttributes = new RegExp( /[^\="]\{\{(.*?)\}\}[^\"]/gm );
		}

		this.tokensThatRequireUser = [];
	}

	/**
	 * Saves all the tokens available in the .tokens property
	 *
	 * @return {undefined}
	 */
	getData() {
		// Get all tokens
		this.tokens = this.getAllTokens();
	}

	/**
	 * Gets all the tokens that can be used for a given item.
	 * These are also going to be filtered by fieldType
	 *
	 * @return {Array} Groups of tokens
	 */
	getAllTokens() {
		let tokensList = [];

		// Get the common tokens
		const commonTokens = this.getCommonTokens();
		// Add tokens to the main array
		if ( commonTokens.tokens.length > 0 ) {
			tokensList.push( commonTokens );
		}

		// Get the date tokens
		const dateTokens = this.getDateTokens();
		if ( dateTokens.tokens.length > 0 ) {
			tokensList.push( dateTokens );
		}

		// Get the advanced tokens
		const advancedTokens = this.getAdvancedTokens();
		// Add tokens to the main array
		if ( advancedTokens.tokens.length > 0 ) {
			tokensList.push( advancedTokens );
		}

		// Get the formula tokens
		const formulaTokens = this.getFormulaTokens();
		// Add tokens to the main array
		if ( formulaTokens.tokens.length > 0 ) {
			tokensList.push( formulaTokens );
		}

		// Get the triggers tokens
		const triggersTokens = this.getTriggersTokens();
		// Add tokens to the main array
		tokensList = [ ...tokensList, ...triggersTokens ];

		// Add action tokens only to actions and the closure
		if ( ! [ 'trigger', 'user-selector' ].includes( this.itemType ) ) {
			// Get the action tokens
			const actionsTokens = this.getActionsTokens();
			// Add tokens to the main array
			tokensList = [ ...tokensList, ...actionsTokens ];
		}

		return tokensList;
	}

	/**
	 * Filters tokens removing the unsupported ones.
	 * Unsupported tokens are defined using the "unsupportedTokens" property
	 *
	 * @param {Array} tokens The tokens
	 * @return {Array}         The tokens, filtered
	 */
	filterUnsupportedTokens( tokens = [] ) {
		// Check if there are unsupported tokens
		if ( ! isEmpty( this.options.unsupportedTokens ) ) {
			// Filter them
			tokens = tokens.filter( ( token ) => {
				return ! this.options.unsupportedTokens.includes( token.id );
			} );
		}

		return tokens;
	}

	/**
	 * Get all the common tokens that can be used a given item
	 * These tokens are independent of the items in the recipe
	 * They are hardcoded, and vary depending on the characteristics
	 * of the recipe itself (for example, if the recipe is anon or logged-in)
	 * These are also going to be filtered by the fieldType
	 *
	 * @return {Object} Group of tokens
	 */
	getCommonTokens() {
		// Create common tokens
		const commonTokens = {
			id: 'common',
		};

		// Add the name
		if ( this.options.includeGroupName ) {
			commonTokens.name = UncannyAutomator.i18n.tokens.global.common;
		}

		// Add the icon
		if ( this.options.includeGroupIcon ) {
			commonTokens.icon = $( `
				<uo-icon id="bolt"></uo-icon>
			` );
		}

		// Add the tokens
		if ( this.options.includeGroupTokens ) {
			commonTokens.tokens = [];

			// Check if it's a field in a trigger or user-selector section,
			// in an anonymous recipe then don't add user related tokens
			if (
				! (
					UncannyAutomator.recipe.type == 'anonymous' &&
					( this.itemType == 'trigger' || this.itemType == 'user-selector' )
				)
			) {
				// Add user related tokens
				commonTokens.tokens.push(
					new Token( {
						id: 'user_id',
						type: 'int',
						name: UncannyAutomator.i18n.tokens.global.userID,
						requiresUser: true,
					} ),
					new Token( {
						id: 'user_username',
						type: 'text',
						name: UncannyAutomator.i18n.tokens.global.userUsername,
						requiresUser: true,
					} ),
					new Token( {
						id: 'user_firstname',
						type: 'text',
						name: UncannyAutomator.i18n.tokens.global.userFirstName,
						requiresUser: true,
					} ),
					new Token( {
						id: 'user_lastname',
						type: 'text',
						name: UncannyAutomator.i18n.tokens.global.userLastName,
						requiresUser: true,
					} ),
					new Token( {
						id: 'user_email',
						type: 'email',
						name: UncannyAutomator.i18n.tokens.global.userEmail,
						requiresUser: true,
					} ),
					new Token( {
						id: 'user_displayname',
						type: 'text',
						name: UncannyAutomator.i18n.tokens.global.userDisplay,
						requiresUser: true,
					} ),
					new Token( {
						id: 'user_role',
						type: 'text',
						name: UncannyAutomator.i18n.tokens.global.userRole,
						requiresUser: true,
					} ),
					new Token( {
						id: 'reset_pass_link',
						type: 'url',
						name: UncannyAutomator.i18n.tokens.global.userResetPassLink,
						requiresUser: true,
					} )
				);
			}

			// Add other common tokens
			commonTokens.tokens.push(
				new Token( {
					id: 'admin_email',
					type: 'email',
					name: UncannyAutomator.i18n.tokens.global.adminEmail,
				} ),
			);

			// Current blog id
			if ( UncannyAutomator.isMultisite && ( isDefined( UncannyAutomator.wp ) && UncannyAutomator.wp ) ) {
				// Current blog id
				commonTokens.tokens.push(
					new Token({
						id: 'current_blog_id',
						type: 'int',
						name: UncannyAutomator.i18n.tokens.global.currentBlogId,
						cacheable: true,
						requiresUser: false,
					})
				);
			}

			// Add other common tokens
			commonTokens.tokens.push(
				new Token( {
					id: 'site_name',
					type: 'text',
					name: UncannyAutomator.isMultisite ? UncannyAutomator.i18n.tokens.global.currentSiteName : UncannyAutomator.i18n.tokens.global.siteName,
				} ),
				new Token( {
					id: 'site_tagline',
					type: 'text',
					name: UncannyAutomator.isMultisite ? UncannyAutomator.i18n.tokens.global.currentSiteTagline : UncannyAutomator.i18n.tokens.global.siteTagline,
				} ),
				new Token( {
					id: 'site_url',
					type: 'url',
					name: UncannyAutomator.isMultisite ? UncannyAutomator.i18n.tokens.global.currentSiteUrl : UncannyAutomator.i18n.tokens.global.siteUrl,
				} ),
				new Token( {
					id: 'recipe_name',
					name: UncannyAutomator.i18n.tokens.global.recipeName,
				} ),
				new Token( {
					id: 'recipe_id',
					type: 'int',
					name: UncannyAutomator.i18n.tokens.global.recipeId,
				} ),
				new Token( {
					id: 'user_ip_address',
					name: UncannyAutomator.i18n.tokens.global.user_ip_address,
				} ),
				new Token( {
					id: 'user_reset_pass_url',
				   name: UncannyAutomator.i18n.tokens.global.user_reset_pass_url,
				} ),
			);
		}

		// Filter tokens
		commonTokens.tokens = this.filterUnsupportedTokens( commonTokens.tokens );

		// Check what tokens require user data
		commonTokens.tokens.forEach( ( token ) => {
			// Check if it requires user data
			if ( token.requiresUser ) {
				this.tokensThatRequireUser.push( token.id );
			}
		} );

		return commonTokens;
	}

	/**
	 * Get all the advanced tokens that can be used a given item
	 * These tokens are independent of the items in the recipe
	 * They are hardcoded, and vary depending on the characteristics
	 * of the recipe itself (for example, if the recipe is anon or logged-in)
	 * These are also going to be filtered by the fieldType
	 *
	 * @since 3.0
	 * @return {Object} Group of tokens
	 */
	getAdvancedTokens() {
		// Create common tokens
		const advancedTokens = {
			id: 'advanced',
			tokens: [],
		};

		// Add the name
		if ( this.options.includeGroupName ) {
			advancedTokens.name = UncannyAutomator.i18n.tokens.global.advanced;
		}

		// Add the icon
		if ( this.options.includeGroupIcon ) {
			advancedTokens.icon = $( `
				<uo-icon id="bolt"></uo-icon>
			` );
		}

		// Add the tokens
		if ( this.options.includeGroupTokens ) {
			// Add the Post Meta token
			advancedTokens.tokens.push(
				new PostMetaToken( {
					// The context of this token
					// We're going to use this to filter the tokens available in the fields
					// on the modal of this token
					context: this.itemType,
				} )
			);

			// Check if it's a field in a trigger or user-selector section,
			// in an anonymous recipe then don't add user related tokens
			if (
				! (
					UncannyAutomator.recipe.type == 'anonymous' &&
					( this.itemType == 'trigger' || this.itemType == 'user-selector' )
				)
			) {
				// Add the User Meta token
				advancedTokens.tokens.push(
					new UserMetaToken( {
						// The context of this token
						// We're going to use this to filter the tokens available in the fields
						// on the modal of this token
						context: this.itemType,
					} )
				);

				if ( isDefined( UncannyAutomator.wp ) && UncannyAutomator.wp === '1' ) {
					advancedTokens.tokens.push(
						new Token( {
							id: 'AUTOLOGINLINK',
							type: 'text',
							name: UncannyAutomator.i18n.tokens.global.autoLoginLink,
							cacheable: true,
							requiresUser: true,
						} )
					);
				}
			}

			// Recipe Run # (total)
			advancedTokens.tokens.push(
				new Token( {
					id: 'recipe_total_run',
					type: 'int',
					name: UncannyAutomator.i18n.tokens.global.recipeTotalRunToken,
					cacheable: true,
					requiresUser: false,
				} )
			);

			// Recipe Run # (current user)
			advancedTokens.tokens.push(
				new Token( {
					id: 'recipe_run',
					type: 'int',
					name: UncannyAutomator.i18n.tokens.global.recipeRunToken,
					cacheable: true,
					requiresUser: false,
				} )
			);

			// Filter tokens
			// advancedTokens.tokens = this.filterTokensByType( advancedTokens.tokens, this.fieldType );
		}

		// Filter tokens
		advancedTokens.tokens = this.filterUnsupportedTokens( advancedTokens.tokens );

		// Check what tokens require user data
		advancedTokens.tokens.forEach( ( token ) => {
			// Check if it requires user data
			if ( token.requiresUser ) {
				this.tokensThatRequireUser.push( token.id );
			}
		} );

		return advancedTokens;
	}

	/**
	 * Returns all date related core tokens
	 * 
	 * @return {Object} Group of tokens
	 */
	getDateTokens() {
		// Create common tokens
		const dateTokens = {
			id: 'date',
			tokens: [],
		};

		// Add the name
		if ( this.options.includeGroupName ) {
			dateTokens.name = UncannyAutomator.i18n.tokens.global.date;
		}

		// Add the icon
		if ( this.options.includeGroupIcon ) {
			dateTokens.icon = $( `
				<uo-icon id="calendar"></uo-icon>
			` );
		}

		// Add the tokens
		if ( this.options.includeGroupTokens ) {
			dateTokens.tokens.push(
				new Token( {
					id: 'current_date',
					name: UncannyAutomator.i18n.tokens.global.currentDate,
					type: 'date'
				} ),
				new Token( {
					id: 'current_time',
					name: UncannyAutomator.i18n.tokens.global.currentTime,
					type: 'time'
				} ),
				new Token( {
					id: 'current_date_and_time',
					name: UncannyAutomator.i18n.tokens.global.currentDateAndTime,
					type: 'date'
				} ),
				new Token( {
					id: 'current_unix_timestamp',
					type: 'int',
					name: UncannyAutomator.i18n.tokens.global.current_unix_timestamp,
					cacheable: true,
					requiresUser: false,
				} ),
				new Token( {
					id: 'currentdate_unix_timestamp',
					type: 'int',
					name: UncannyAutomator.i18n.tokens.global.currentdate_unix_timestamp,
					cacheable: true,
					requiresUser: false,
				} )
			);
		}

		// Filter tokens
		dateTokens.tokens = this.filterUnsupportedTokens( dateTokens.tokens );

		// Check what tokens require user data
		dateTokens.tokens.forEach( ( token ) => {
			if ( token.requiresUser ) {
				this.tokensThatRequireUser.push( token.id );
			}
		} );

		return dateTokens;
	}

	/**
	 * Returns the available formula tokens
	 */
	getFormulaTokens() {
		// Create common tokens
		const formulaTokens = {
			id: 'formulas',
			tokens: [],
		};

		// Add the name
		if ( this.options.includeGroupName ) {
			formulaTokens.name = UncannyAutomator.i18n.tokens.global.formulas;
		}

		// Add the icon
		if ( this.options.includeGroupIcon ) {
			formulaTokens.icon = $( `
				<uo-icon id="code"></uo-icon>
			` );
		}

		// Add the tokens
		if ( this.options.includeGroupTokens ) {
			// Add the Post Meta token
			formulaTokens.tokens.push(
				new CalculationToken( {
					// The context of this token
					// We're going to use this to filter the tokens available in the fields
					// on the modal of this token
					context: this.itemType,
				} )
			);
		}

		// Filter tokens
		formulaTokens.tokens = this.filterUnsupportedTokens( formulaTokens.tokens );

		// Check what tokens require user data
		formulaTokens.tokens.forEach( ( token ) => {
			// Check if it requires user data
			if ( token.requiresUser ) {
				this.tokensThatRequireUser.push( token.id );
			}
		} );

		return formulaTokens;
	}

	/**
	 * Returns the tokens of all triggers
	 *
	 * @since 3.0
	 * @return {Array} Array with multiple groups of tokens
	 */
	getTriggersTokens() {
		const itemsTokens = [];

		const items = $.extend(
			{},
			items,
			itemsObject( 'get', {
				filter: {
					itemType: 'trigger',
				},
			} )
		);

		// Iterate items
		// eslint-disable-next-line no-unused-vars
		Object.entries( items ).forEach( ( [ itemID, item ] ) => {
			// Get the item tokens
			const itemTokens = this.getItemTokens( item );

			// Check if it has tokens
			if ( itemTokens.tokens.length > 0 || ! this.options.includeGroupTokens ) {
				itemsTokens.push( itemTokens );
			}
		} );

		return itemsTokens;
	}

	/**
	 * Returns the tokens from the actions. If there is an item ID defined, it will filter tokens 
	 * so it returns only those available for that particular action
	 * 
	 * @return {Array} with multiple group of tokens
	 */
	getActionsTokens() {
		// The tokens groups
		const tokenGroups = [];

		// Get the actions
		let actions;
		// Check if there is an item ID defined, and the item is an action
		if ( 
			[ 'condition', 'action' ].includes( this.itemType ) && 
			! isEmpty( this.options.itemID )	
		) {
			actions = getActionsThatRunBefore( this.options.itemID );
		} else {
			// Get all
			actions = getActions( {} );
		}

		// Iterate actions
		// eslint-disable-next-line no-unused-vars
		Object.entries( actions ).forEach( ( [ actionID, action ] ) => {
			// Get the item tokens
			const actionTokens = this.getItemTokens( action );

			// Check if it has tokens
			if ( actionTokens.tokens.length > 0 || ! this.options.includeGroupTokens ) {
				tokenGroups.push( actionTokens );
			}
		} );
		
		return tokenGroups;
	}

	/**
	 * Get all the tokens generated by a given item
	 * The tokens added by this method can be divided into two groups:
	 * - Automatic tokens: These are created using the fields of
	 *   the trigger/action. For example, if the item has a dropdown
	 *   to select a page, with the label "Page", then this method will
	 *   create a text token called "Page".
	 * - Custom dynamic tokens: These are created dynamically with PHP.
	 *   For example, a trigger that lets you select a Gravity Form would
	 *   have the fields as tokens.
	 *
	 * @param {Object} item The item
	 * @return {Object}       Group of tokens
	 */
	getItemTokens( item ) {
		// Create the tokens group
		const tokensGroup = {
			id: item.itemCode,
			integrationId: item.integrationCode,
			itemID: item.id,
			itemType: item.itemType,
		};

		// Add the name
		if ( this.options.includeGroupName ) {
			// Get the name of the groups of tokens
			// For this, we will use the item sentence
			let itemSentence = item.sentence;
			if ( ! item.isMissing ) {
				itemSentence = getGlobalItem( item.integrationCode, item.itemType, item.itemCode ).sentence;
			}

			// Get or create the parsed item sentence
			tokensGroup.name = parseStringWithTokens(
				item,
				itemSentence,
				UncannyAutomator.recipe.items[ item.id ].meta
			).html;
		}

		// Add the icon
		if ( this.options.includeGroupIcon ) {
			// Create the icon
			let $icon = $( `<img src="${ getIntegrationIconURL( { integrationCode: item.integrationCode } ) }">` );

			// Add the icon
			tokensGroup.icon = $icon;
		}

		// Add the tokens
		if ( this.options.includeGroupTokens ) {
			let itemTokens = [];

			// FIELD tokens
			// Create tokens using the fields
			// Iterate the groups of options
			// eslint-disable-next-line no-unused-vars
			Object.entries( item.options ).forEach( ( [ optionsGroupID, optionsGroup ] ) => {
				// Iterate each field
				optionsGroup.fields.forEach( ( optionField ) => {
					// Get the field attributes
					const fieldAttributes = optionField.getAttributes();

					// Check the field type
					// Check if it's a repeater field
					// Check the field type
					if ( fieldAttributes.fieldType == 'repeater' ) {
						// Iterate the rows
						optionField.fieldRows.forEach( ( row, index ) => {
							// Get the row index
							const rowIndex = index;

							// Iterate the row fields
							row.forEach( ( childField ) => {
								// Add field
								itemTokens.push( {
									// Create the token
									id: this.getItemTokenID( {
										itemType: item.itemType,
										itemID: item.id,
										tokenParentCode: item.itemCode,
										// REPEATER_FIELD_ID | ROW_INDEX | FIELD_ID
										tokenCode: `${
											fieldAttributes.optionCode
										}|${ rowIndex }|${ childField.getAttributes().optionCode }`,
										isField: true,
									} ),

									// childField name:
									// REPEATER_FIELD_NAME - "Row" ROW_INDEX - FIELD_NAME
									name: `${ fieldAttributes.tokenName } - ${ sprintf(
										UncannyAutomator.i18n.rowNumber,
										( rowIndex + 1 )
									) } - ${ childField.getAttributes().tokenName }`,
									type:
										childField.getAttributes().fieldType == 'select' ||
										childField.getAttributes().fieldType == 'textarea'
											? 'text'
											: childField.getAttributes().fieldType,
									value: childField.getValue(),
									fieldId: childField.getAttributes().optionCode,
									itemID: item.id,
									itemType: item.itemType,
									cacheable: false,
								} );
							} );
						} );
					} else {
						// Check if it has custom field tokens, also called "relevant tokens"
						// eslint-disable-next-line no-lonely-if
						if (
							isDefined( fieldAttributes.customFieldTokens ) &&
							isObject( fieldAttributes.customFieldTokens )
						) {
							// Iterate the relevant tokens
							Object.entries( fieldAttributes.customFieldTokens ).forEach( ( [ tokenId, tokenName ] ) => {
								// Get the token type of the "relevant tokens"
								let tokenType = ( fieldAttributes.fieldType == 'select' || fieldAttributes.fieldType == 'textarea' ) ? 'text' : fieldAttributes.fieldType;

								// Check if we have to override the token type based on the token ID
								if ( tokenId.endsWith( '_ID' ) ) {
									tokenType = 'int';
								}

								if ( tokenId.endsWith( '_DATE' ) ) {
									tokenType = 'date';
								}

								if ( tokenId.endsWith( '_TIME' ) ) {
									tokenType = 'time';
								}

								if ( tokenId.endsWith( '_URL' ) ) {
									tokenType = 'url';
								}

								if ( tokenId.endsWith( '_EMAIL' ) ) {
									tokenType = 'email';
								}

								itemTokens.push( {
									id: this.getItemTokenID( {
										itemType: item.itemType,
										itemID: item.id,
										tokenParentCode: item.itemCode,
										tokenCode: tokenId,
										isField: true,
									} ),
									name: tokenName,
									type: tokenType,
									value: optionField.getValue(),
									fieldId: fieldAttributes.optionCode,
									itemID: item.id,
									cacheable: true,
								} );
							} );
						} else {
							// Create a token using the field label
							itemTokens.push( {
								id: this.getItemTokenID( {
									itemType: item.itemType,
									itemID: item.id,
									tokenParentCode: item.itemCode,
									tokenCode: fieldAttributes.optionCode,
									isField: true,
								} ),
								name: fieldAttributes.tokenName,
								type:
									fieldAttributes.fieldType == 'select' || fieldAttributes.fieldType == 'textarea'
										? 'text'
										: fieldAttributes.fieldType,
								value: optionField.getValue(),
								fieldId: fieldAttributes.optionCode,
								itemID: item.id,
								cacheable: true,
							} );
						}
					}
				} );
			} );

			// Custom ITEM tokens
			// These are dynamic tokens, for example, the fields of a form
			item.tokens.forEach( ( token ) => {
				itemTokens.push( {
					id: this.getItemTokenID( {
						itemType: item.itemType,
						itemID: item.id,
						tokenParentCode: isDefined( token.tokenParent ) ? token.tokenParent : token.tokenIdentifier,
						tokenCode: token.tokenId,
						isField: false,
					} ),
					name: token.tokenName,
					type: isDefined( token.tokenType ) ? token.tokenType : 'text',
					itemID: item.id,
					cacheable: true,
				} );
			} );

			// Add the tokens to the group
			tokensGroup.tokens = itemTokens;

			// Filter tokens
			tokensGroup.tokens = this.filterUnsupportedTokens( tokensGroup.tokens );

			// Filter tokens using a custom function (if it has one)
			if ( ! isEmpty( item.filterTokens ) ) {
				// Try to overwrite empty function
				try {
					// Try to convert the string to function, but first remove the <script> tags and trim the string
					// eslint-disable-next-line no-eval
					const filterTokens = eval(
						`(${ item.filterTokens.replace( '<script>', '' ).replace( '</script>', '' ).trim() })`
					);

					// Filter the tokens
					itemTokens = filterTokens( tokensGroup, item );
				} catch ( e ) {
					// Continue regardless of error

					// Output error if the developer mode is enabled
					if ( UncannyAutomator.dev.developerMode ) {
						// eslint-disable-next-line no-console
						console.warn( e );
						// eslint-disable-next-line no-console
						console.log( item.filterTokens );
					}
				}
			}

			// Filter tokens with the global filter
			// This will filter the tokens by type
			/*
			tokensGroup.tokens = this.filterTokensByType(
				tokensGroup.tokens,
				this.fieldType
			);
			*/
		}

		// Add the group of tokens
		return tokensGroup;
	}

	/**
	 * Filters the tokens based on the field type
	 *
	 * @param {Array} tokens The tokens to be filtered
	 * @return {Array} tokens The tokens, filtered
	 */
	filterTokensByType( tokens ) {
		// Check if the field type is defined
		// Otherwise, don't filter the tokens
		if ( isDefined( this.fieldType ) ) {
			// Check if it's NOT a text, textarea or url field
			// We don't have to filter tokens for those
			if ( ! [ 'text', 'textarea', 'url' ].includes( this.fieldType ) ) {
				// Iterate the tokens
				tokens = tokens.filter( ( token ) => {
					// Get the token type
					let tokenType = token.type;

					// Check if the field is a "float" field. In that case,
					// promote all the "int" tokens to "float". All integers
					// are floats
					if ( this.fieldType == 'float' && tokenType == 'int' ) {
						tokenType = 'float';
					}

					// Check if the token type matches the field type
					return this.fieldType == tokenType;
				} );
			}
		}

		return tokens;
	}

	/**
	 * Creates a DOM node with the token data
	 *
	 * @param {String} tokenID A token ID
	 * @return {Node}           The token node
	 */
	createTokenDOMNode( tokenID ) {
		// Default response
		let $tokenNode = null;

		// Get more information about the token
		const token = this.getTokenDataByID( tokenID );

		// Check if the token is valid
		if ( isDefined( token ) ) {
			// Check if we already created this element
			if ( isDefined( UncannyAutomator.cache.tokens[ token.id ] ) ) {
				$tokenNode = UncannyAutomator.cache.tokens[ token.id ];
			} else {
				// Create the token node
				// VanillaJS because of the performance, this might be heavy
				$tokenNode = document.createElement( 'span' );
				$tokenNode.classList.add( 'uap-token' );

				// Create icon
				const $tokenIcon = document.createElement( 'span' );
				$tokenIcon.classList.add( 'uap-token__icon' );
				$tokenIcon.appendChild( token.group.icon[ 0 ].cloneNode() );

				// Insert the icon
				$tokenNode.appendChild( $tokenIcon );

				// Insert the name
				const $tokenName = document.createElement( 'span' );
				$tokenName.classList.add( 'uap-token__name' );
				$tokenName.innerHTML = token.name;
				$tokenNode.appendChild( $tokenName );

				// Check if this is a item token
				if ( isDefined( token.itemID ) ) {
					// Save the token action
					const tokenAction = UncannyAutomator.recipe.items[ token.itemID ];

					// Check if we have to add the item ID
					if ( tokenAction.shouldShowItemId ) {
						$tokenName.insertAdjacentHTML( 'afterBegin', `<span class="uap-token__id">${ sprintf( '#%s: ', token.itemID ) }</span>` );
					}

					// Check if it's an action
					if ( tokenAction.itemType === 'action' ) {
						$tokenNode.classList.add( 'uap-token--action' );

						const $flags = document.createElement( 'span' );
						$flags.classList.add( 'uap-token__flags' );

						// Check if the action is inside a condition block
						if ( tokenAction.hasConditions ) {
							$tokenNode.classList.add( 'uap-token--action-conditions', 'uap-token--warning' );

							$flags.insertAdjacentHTML( 
								'beforeEnd', 
								`<uo-icon id="code-merge" slot="target"></uo-icon>` 
							);
						}

						// Check if this is being added to a field
						if ( 
							isDefined( this.Field ) &&
							isDefined( this.Field.attributes.itemId ) &&
							isDefined( UncannyAutomator.recipe.items[ this.Field.attributes.itemId ] )
						) {
							this.destinationItemID = this.Field.attributes.itemId;
						}

						// Check if the destination is an action
						if ( isDefined( this.destinationItemID ) &&
							isDefined( UncannyAutomator.recipe.items[ this.destinationItemID ] ) &&
							UncannyAutomator.recipe.items[ this.destinationItemID].itemType === 'action'
						) {
							// Save the destination action
							const destinationItem = UncannyAutomator.recipe.items[ this.destinationItemID ];

							// Check if the action where the token is coming from is scheduled, BUT THE DESTINATION action is not
							// This means the token MIGHT not be available
							if ( tokenAction.isScheduled && ! destinationItem.isScheduled ) {
								$tokenNode.classList.add( 'uap-token--action-scheduled', 'uap-token--warning' );

								$flags.insertAdjacentHTML( 
									'beforeEnd', 
									`<uo-icon id="clock" slot="target"></uo-icon>` 
								);
							}

							if ( tokenAction.isDelayed && ! destinationItem.isDelayed ) {
								$tokenNode.classList.add( 'uap-token--action-delayed', 'uap-token--warning' );

								$flags.insertAdjacentHTML( 
									'beforeEnd', 
									`<uo-icon id="clock" slot="target"></uo-icon>` 
								);
							}
						}

						$tokenNode.append( $flags );
					}
				}

				// Check if we can save the token in the cache
				if ( token.cacheable ) {
					// Save node in the cache
					UncannyAutomator.cache.tokens[ token.id ] = $tokenNode;
				}
			}

			// Clone the node
			$tokenNode = $tokenNode.cloneNode( true );
		} else {
			// Create the token node
			// VanillaJS because of the performance, this might be heavy
			$tokenNode = document.createElement( 'span' );
			$tokenNode.classList.add( 'uap-token', 'uap-token--invalid' );

			// Insert the icon
			const $tokenIcon = document.createElement( 'uo-icon' );
			$tokenIcon.setAttribute( 'id', 'exclamation-triangle' );
			$tokenNode.appendChild( $tokenIcon );

			// Insert the name
			const $tokenName = document.createElement( 'span' );
			$tokenName.classList.add( 'uap-token__name' );
			$tokenName.innerHTML = tokenID;
			$tokenNode.appendChild( $tokenName );
		}

		return $tokenNode;
	}

	/**
	 * Returns more data about a token by just using the token ID
	 *
	 * @param {String} tokenID The token ID
	 * @return {Object}        The token data
	 */
	getTokenDataByID( tokenID = '' ) {
		// Default response
		let token = null;

		// Check if the tokenID is not empty
		if ( ! isEmpty( tokenID ) ) {
			// Iterate the groups of tokens
			this.tokens.some( ( tokensGroup ) => {
				// Get the token using the tokenID
				token = tokensGroup.tokens.reduce( ( locatedToken, currentToken ) => {
					if ( isDefined( currentToken.is ) ) {
						return currentToken.is( tokenID ) ? currentToken : locatedToken;
					} else {
						return currentToken.id == tokenID ? currentToken : locatedToken;
					}
				}, null );

				// Check if we found a token
				if ( isDefined( token ) ) {
					// Check if it has a variation generator
					if ( isDefined( token ) && isDefined( token.getVariation ) ) {
						token = token.getVariation( tokenID );
					}

					// Add more data to the token
					token.group = {
						id: tokensGroup.id,
						name: tokensGroup.name,
						icon: tokensGroup.icon,
					};

					return true;
				} else {
					return false;
				}
			} );
		}

		return token;
	}

	/**
	 * Render tokens in text
	 *
	 * @param {String} textWithTokens The text
	 * @param {Object} options        Optional options
	 * @return {Node}                  Node with the text with rendered tokens
	 */
	renderTextWithTokens( textWithTokens, options = {} ) {
		// Get the options
		options = Object.assign(
			{
				template: '',
				parseTokensInAttributes: true,
				returnString: false
			},
			options
		);

		// Check if there is a destination item ID
		if ( isDefined( options.itemID ) ) {
			this.destinationItemID = options.itemID;
		}

		// Check if it has a template
		const hasTemplate = ! isEmpty( options.template );
		
		// Create callback
		const replaceTokensCallback = ( fullMatch, token ) => {
			// Get token node
			const $token = this.createTokenDOMNode( token );

			// Get the token node HTML
			let tokenHTML = $token.outerHTML;

			// Check if we have to add a wrapper to it
			if ( hasTemplate ) {
				// Add the variable data to the template
				tokenHTML = sprintf( options.template, tokenHTML, token );
			}

			// Replace the content with the HTML of the token node
			// Verify that we're only replacing the content inside {{}},
			// and that we're not removing any other characters
			return fullMatch.replace( `{{${ token }}}`, tokenHTML );
		};

		// Replace the tokens in the text with tokens
		textWithTokens = textWithTokens.replace(
			// Get the correct regex
			options.parseTokensInAttributes ? this.tokensRegEx : this.tokensRegExNoAttributes,
			// Replace the content
			replaceTokensCallback
		);

		// Check if we have to return just the string
		if ( options.returnString ) {
			return textWithTokens;
		}

		// Check if we have to return a node
		if ( ! options.returnString ) {
			// Create the container
			const $container = document.createElement( 'span' );
			$container.classList.add( 'uap-text-with-tokens' );

			// Add the text, now with the tokens rendered, to the container
			$container.innerHTML = textWithTokens;

			return $container;
		}
	}

	/**
	 * Generates the token ID
	 */
	getItemTokenID( { itemType, itemID, tokenParentCode, tokenCode, isField = false } ) {
		let tokenID = '';

		switch ( itemType ) {
			case 'trigger':
				tokenID = `${ itemID }:${ tokenParentCode }:${ tokenCode }`;
				break;

			case 'action':
				// Get prefix
				const tokenPrefix = isField ? 'ACTION_FIELD' : 'ACTION_META';

				tokenID = `${ tokenPrefix }:${ itemID }:${ tokenParentCode }:${ tokenCode }`;
				break;
		}

		return tokenID;
	}
}

/**
 * Creates a single token
 */
class Token {
	/**
	 * Saves data in the instance
	 *
	 * @param {Object}  options              The options
	 * @param {String}  options.id           The token ID
	 * @param {String}  options.type         The token type (similar to the field type, with some exceptions)
	 * @param {String}  options.name         The token name
	 * @param {Boolean} options.cacheable    Whether Automator should cache the token node
	 * @param {Boolean} options.requiresUser Whether the token requires user data to be used
	 * @return {undefined}
	 */
	constructor( { id, type = 'text', name, cacheable = true, requiresUser = false } ) {
		// Save data
		this.id = id;
		this.type = type;
		this.name = name;
		this.cacheable = cacheable;
		this.requiresUser = requiresUser;
	}

	/**
	 * Returns true if the passed token ID matches the ID of the token instance
	 *
	 * @param {String} tokenID The token ID
	 * @return {Boolean}         True if tokenID matches the token ID
	 */
	is( tokenID ) {
		return tokenID == this.id;
	}
}

/**
 * Creates the Post Meta token, which shows a modal before being added
 */
class PostMetaToken extends Token {
	/**
	 * Creates the Post Meta token
	 *
	 * @param {Object} options            The options
	 * @param {String} options.customID   The custom ID. Don't define one unless you have to.
	 * @param {String} options.customName The custom name. Don't define one unless you have to.
	 * @param {String} options.context    The context. Define this one. 'trigger', 'action', or 'user-selector', depending on where you're using the token
	 * @return {undefined}
	 */
	constructor( { customID, customName, context = 'trigger' } ) {
		super( {
			id: isDefined( customID ) ? customID : 'POSTMETA:POSTID:KEY',
			name: isDefined( customName ) ? customName : UncannyAutomator.i18n.tokens.global.postMetaKey,
			type: 'text',
			cacheable: false,
			requiresUser: false,
		} );

		// Save context
		this.context = context;
	}

	/**
	 * Returns the default token ID
	 *
	 * @return {String} The default token ID
	 */
	get unmodifiedID() {
		return 'POSTMETA:POSTID:KEY';
	}

	/**
	 * Get the name of the token when it doesn't have data
	 *
	 * @return {String} The default name
	 */
	get defaultName() {
		return UncannyAutomator.i18n.tokens.global.postMetaKey;
	}

	/**
	 * Callback invoked before adding the token to the field
	 *
	 * @param {Callback} addTokenToField Callback invoked after validating fields
	 * @return {undefined}
	 */
	beforeAdded( addTokenToField ) {
		// Create the content for the modal
		const $modalContent = $( `<div></div>` );

		// Post field "Learn more" URL
		const tokenPostFieldLearnMore = addUTMParameters( 'https://automatorplugin.com/knowledge-base/post-meta-tokens/', {
			medium: 'add_token',
			content: 'post_meta_post_id_learn_more',
		} );

		// Create the "Post" field
		const tokenPostField = new Field( 'int', {
			itemType: this.context,
			optionCode: 'token__postMetaPostField',
			label: UncannyAutomator.i18n.tokens.specialTokens.postMeta.modal.postFieldLabel,
			description: sprintf(
				UncannyAutomator.i18n.tokens.specialTokens.postMeta.modal.postFieldDescription,
				`<a href="${ tokenPostFieldLearnMore }" target="_blank">${ UncannyAutomator.i18n.learnMore } <uo-icon id="external-link"></uo-icon></a>`
			),
			isRequired: true,
			supportsTokens: true,
			unsupportedTokens: [ 'USERMETA:KEY', 'POSTMETA:POSTID:KEY' ],
		} );

		// Add the field
		$modalContent.append( tokenPostField.createFormElement() );

		// Create the "Key" field
		const tokenKeyField = new Field( 'text', {
			itemType: this.context,
			optionCode: 'token__postMetaKeyField',
			label: UncannyAutomator.i18n.tokens.specialTokens.postMeta.modal.keyFieldLabel,
			description: UncannyAutomator.i18n.tokens.specialTokens.postMeta.modal.keyFieldDescription,
			isRequired: true,
			supportsTokens: true,
			unsupportedTokens: [ 'USERMETA:KEY', 'POSTMETA:POSTID:KEY' ],
		} );

		// Add the field
		$modalContent.append( tokenKeyField.createFormElement() );

		// AFTER we added the elements to the DOM let's call init
		// TokenSelector and CodeMirror. We're going to call this in
		// ItemOptions.js because the TokenSelector and
		// CodeMirror need the item in the DOM before changing it
		tokenPostField.initTokenSelector();
		tokenPostField.initCodeMirror();
		tokenKeyField.initTokenSelector();
		tokenKeyField.initCodeMirror();

		// Show a modal
		const modal = new Modal(
			{
				title: UncannyAutomator.i18n.tokens.specialTokens.postMeta.modal.title,
				content: $modalContent,
				buttons: {
					cancel: UncannyAutomator.i18n.cancel,
					confirm: UncannyAutomator.i18n.tokens.specialTokens.postMeta.modal.confirmButton,
				},
			},
			{
				size: 'extra-large',
				allowContentOverflow: true,
			}
		);

		modal.setEvents( {
			afterSetEvents: () => {
				modal.show();
			},
			onConfirm: () => {
				// Validate the field
				const postValidation = tokenPostField.validate(),
					keyValidation = tokenKeyField.validate();

				// Check if the fields are valid
				if ( postValidation.isValid && keyValidation.isValid ) {
					// Get the values
					const postMetaPost = tokenPostField.getValue(),
						postMetaKey = tokenKeyField.getValue();

					// Clone the token data, don't modify the original
					const postMetaToken = $.extend( true, {}, this );

					// Espace tokens characters from field values
					// Check if it's using tokens, and replace the reserved characters to prevent validation issues.
					const postMetaPostEscaped = postMetaPost
						.replaceAll( '{', '[' )
						.replaceAll( '}', ']' )
						.replaceAll( ':', ';' );

					const postMetaKeyEscaped = postMetaKey
						.replaceAll( '{', '[' )
						.replaceAll( '}', ']' )
						.replaceAll( ':', ';' );

					// Change ID of the token
					postMetaToken.id = postMetaToken.id
						// Replace POSTID with the value of the "Post" field
						.replace( 'POSTID', postMetaPostEscaped )
						// Replace KEY with the value of the "Meta key" field
						.replace( 'KEY', postMetaKeyEscaped );

					// Create the token name
					let tokenName = sprintf(
						UncannyAutomator.i18n.tokens.global.postMetaKeyTemplate,
						postMetaPostEscaped,
						postMetaKeyEscaped
					);

					// Check if one of the fields have tokens
					if ( postMetaPost.includes( '{{' ) || postMetaKey.includes( '{{' ) ) {
						// Try to parse tokens inside the token (beta, catch possible errors and add fallback)
						try {
							// Parse tokens
							const tokensInFields = new Tokens( 'action' );

							// Visually parse tokens in the "Post ID" and "Meta key" fields
							const postMetaPostNode = tokensInFields.renderTextWithTokens( postMetaPost ),
								postMetaKeyNode = tokensInFields.renderTextWithTokens( postMetaKey );

							// Create the token name
							tokenName = sprintf(
								UncannyAutomator.i18n.tokens.global.postMetaKeyTemplate,
								postMetaPostNode.outerHTML,
								postMetaKeyNode.outerHTML,
							);
						} catch ( e ) {
							// Output error if the developer mode is enabled
							if ( UncannyAutomator.dev.developerMode ) {
								// eslint-disable-next-line no-console
								console.warn( e );
							}
						}
					}

					// Change name of the token
					postMetaToken.name = tokenName;

					// Destroy the modal
					modal.destroy();

					// Invoke the callback
					addTokenToField( postMetaToken );
				}
			},
			afterHide: () => {
				modal.destroy();
			},
		} );
	}

	/**
	 * Generates the dynamic token name based on the data in the token ID
	 *
	 * @param {String} tokenID The token ID
	 * @return {String}         The token name, given the data in the ID
	 */
	getDynamicTokenName( tokenID ) {
		// Get the parts of the token
		// eslint-disable-next-line no-unused-vars, @wordpress/no-unused-vars-before-return
		const [ postMetaBase, postMetaPost, postMetaKey ] = tokenID.split( ':' );

		// Create the token name
		let tokenName = sprintf(
			UncannyAutomator.i18n.tokens.global.postMetaKeyTemplate,
			postMetaPost,
			postMetaKey,
		);

		// Check if one of the fields have tokens
		if ( postMetaPost.includes( '[[' ) || postMetaKey.includes( '[[' ) ) {
			// Try to parse tokens inside the token (beta, catch possible errors and add fallback)
			try {
				// Parse tokens
				const tokensInFields = new Tokens( 'action' );

				// Visually parse tokens in the "Post ID" and "Meta key" fields
				const postMetaPostNode = tokensInFields.renderTextWithTokens(
						postMetaPost.replaceAll( '[', '{' ).replaceAll( ']', '}' ).replaceAll( ';', ':' )
					),
					postMetaKeyNode = tokensInFields.renderTextWithTokens(
						postMetaKey.replaceAll( '[', '{' ).replaceAll( ']', '}' ).replaceAll( ';', ':' )
					);

				// Create the token name
				tokenName = sprintf(
					UncannyAutomator.i18n.tokens.global.postMetaKeyTemplate,
					postMetaPostNode.outerHTML,
					postMetaKeyNode.outerHTML,
				);
			} catch ( e ) {
				// Output error if the developer mode is enabled
				if ( UncannyAutomator.dev.developerMode ) {
					// eslint-disable-next-line no-console
					console.warn( e );
				}
			}
		}

		return tokenName;
	}

	/**
	 * Returns true if the current token has the passed ID
	 *
	 * @param {String} tokenID The token ID
	 * @return {Boolean} True if the passed ID matches the ID of the current token
	 */
	is( tokenID ) {
		return tokenID.startsWith( 'POSTMETA:' );
	}

	/**
	 * Returns a variation of this token given a token ID with custom data
	 *
	 * @param {String} tokenID The token ID
	 * @return {PostMetaToken} The Post Meta token, with custom data
	 */
	getVariation( tokenID ) {
		// Create a new instance of this class
		const variation = new PostMetaToken( {
			customID: tokenID,
			customName: this.getDynamicTokenName( tokenID ),
			context: this.context,
		} );

		return variation;
	}
}

/**
 * Creates the User Meta token, which shows a modal before being added
 */
class UserMetaToken extends Token {
	/**
	 * Creates the User Meta token
	 *
	 * @param {Object} options            The options
	 * @param {String} options.customID   The custom ID. Don't define one unless you have to.
	 * @param {String} options.customName The custom name. Don't define one unless you have to.
	 * @param {String} options.context    The context. Define this one. 'trigger', 'action', or 'user-selector', depending on where you're using the token
	 * @return {undefined}
	 */
	constructor( { customID, customName, context = 'trigger' } ) {
		super( {
			id: isDefined( customID ) ? customID : 'USERMETA:KEY',
			name: isDefined( customName ) ? customName : UncannyAutomator.i18n.tokens.global.userMetaKey,
			type: 'text',
			cacheable: false,
			requiresUser: true,
		} );

		this.context = context;
	}

	/**
	 * Callback invoked before adding the token to the field
	 *
	 * @param {Callback} addTokenToField Callback invoked after validating fields
	 * @return {undefined}
	 */
	beforeAdded( addTokenToField ) {
		// Create the content for the modal
		const $modalContent = $( `<div></div>` );

		// Create the field
		const metaKeyField = new Field( 'text', {
			optionCode: 'token__metaKeyField',
			label: UncannyAutomator.i18n.tokens.specialTokens.userMeta.modal.userMetaKey,
			description: UncannyAutomator.i18n.tokens.specialTokens.userMeta.modal.userMetaKeyDescription,
			isRequired: true,
		} );

		// Add the field
		$modalContent.append( metaKeyField.createFormElement() );

		// Show a modal
		const modal = new Modal( {
			title: UncannyAutomator.i18n.tokens.specialTokens.userMeta.modal.title,
			content: $modalContent,
			buttons: {
				cancel: UncannyAutomator.i18n.cancel,
				confirm: UncannyAutomator.i18n.tokens.specialTokens.userMeta.modal.confirmButton,
			},
		} );

		modal.setEvents( {
			afterSetEvents: () => {
				modal.show();
			},
			onConfirm: () => {
				// Validate the field
				const fieldValidation = metaKeyField.validate();

				// Get the field value
				const fieldValue = metaKeyField.getValue();

				// Check if the field is valid
				if ( fieldValidation.isValid ) {
					// Clone the token, don't modify the original
					const userMetaToken = $.extend( true, {}, this );

					// Change ID of the token
					userMetaToken.id = userMetaToken.id.replace( 'KEY', fieldValue );

					// Change name of the token
					userMetaToken.name = sprintf(
						UncannyAutomator.i18n.tokens.global.userMetaKeyTemplate,
						fieldValue,
					);

					// Destroy the modal
					modal.destroy();

					// Invoke the callback
					addTokenToField( userMetaToken );
				}
			},
			afterHide: () => {
				modal.destroy();
			},
		} );
	}

	/**
	 * Generates the dynamic token name based on the data in the token ID
	 *
	 * @param {String} tokenID The token ID
	 * @return {String}         The token name, given the data in the ID
	 */
	getDynamicTokenName( tokenID ) {
		// Get the key the user wants to get
		const key = tokenID.replace( 'USERMETA:', '' );

		// Create token name
		const tokenName = sprintf( UncannyAutomator.i18n.tokens.global.userMetaKeyTemplate, key );

		return tokenName;
	}

	/**
	 * Returns true if the current token has the passed ID
	 *
	 * @param {String} tokenID The token ID
	 * @return {Boolean}          True if the passed ID matches the ID of the current token
	 */
	is( tokenID ) {
		return tokenID.startsWith( 'USERMETA:' );
	}

	/**
	 * Returns a variation of this token given a token ID with custom data
	 *
	 * @param {String} tokenID The token ID
	 * @return {UserMetaToken}          The User Meta token, with custom data
	 */
	getVariation( tokenID ) {
		// Create a new instance of this class
		const variation = new UserMetaToken( {
			customID: tokenID,
			customName: this.getDynamicTokenName( tokenID ),
			context: this.context,
		} );

		return variation;
	}
}

/**
 * Creates the Calculation token, which shows a modal before being added
 */
 class CalculationToken extends Token {
	/**
	 * Creates the Post Meta token
	 *
	 * @param {Object} options            The options
	 * @param {String} options.customID   The custom ID. Don't define one unless you have to.
	 * @param {String} options.customName The custom name. Don't define one unless you have to.
	 * @param {String} options.context    The context. Define this one. 'trigger', 'action', or 'user-selector', depending on where you're using the token
	 * @return {undefined}
	 */
	constructor( { customID, customName, context = 'trigger' } ) {
		super( {
			id: isDefined( customID ) ? customID : 'CALCULATION:FORMULA',
			name: isDefined( customName ) ? customName : UncannyAutomator.i18n.tokens.global.calculation,
			type: 'text',
			cacheable: false,
			requiresUser: false,
		} );

		// Save context
		this.context = context;
	}

	/**
	 * Returns the default token ID
	 *
	 * @return {String} The default token ID
	 */
	get unmodifiedID() {
		return 'CALCULATION:FORMULA';
	}

	/**
	 * Get the name of the token when it doesn't have data
	 *
	 * @return {String} The default name
	 */
	get defaultName() {
		return UncannyAutomator.i18n.tokens.global.calculation;
	}

	/**
	 * Callback invoked before adding the token to the field
	 *
	 * @param {Callback} addTokenToField Callback invoked after validating fields
	 * @return {undefined}
	 */
	beforeAdded( addTokenToField ) {
		// Create the content for the modal
		const $modalContent = $( `<div></div>` );

		// Formula field "Learn more" URL
		const tokenFormulaFieldLearnMore = addUTMParameters( 'https://automatorplugin.com/knowledge-base/calculation-tokens/', {
			medium: 'add_token',
			content: 'post_meta_calculation_learn_more',
		} );

		// Create the "Formula" field
		const formulaField = new Field( 'text', {
			itemType: this.context,
			optionCode: 'token__calculationFormulaField',
			label: UncannyAutomator.i18n.tokens.specialTokens.calculation.modal.formulaFieldLabel,
			description: sprintf(
				UncannyAutomator.i18n.tokens.specialTokens.calculation.modal.formulaFieldDescription,
				`<a href="${ tokenFormulaFieldLearnMore }" target="_blank">${ UncannyAutomator.i18n.learnMore } <uo-icon id="external-link"></uo-icon></a>`
			),
			isRequired: true,
			supportsTokens: true,
			unsupportedTokens: [ 'CALCULATION:FORMULA' ],
		} );

		// Add the field
		$modalContent.append( formulaField.createFormElement() );

		// AFTER we added the elements to the DOM let's call init
		// TokenSelector and CodeMirror. We're going to call this in
		// ItemOptions.js because the TokenSelector and
		// CodeMirror need the item in the DOM before changing it
		formulaField.initTokenSelector();
		formulaField.initCodeMirror();

		// Show a modal
		const modal = new Modal(
			{
				title: UncannyAutomator.i18n.tokens.specialTokens.calculation.modal.title,
				content: $modalContent,
				buttons: {
					cancel: UncannyAutomator.i18n.cancel,
					confirm: UncannyAutomator.i18n.tokens.specialTokens.calculation.modal.confirmButton,
				},
			},
			{
				size: 'extra-large',
				allowContentOverflow: true,
			}
		);

		modal.setEvents( {
			afterSetEvents: () => {
				modal.show();
			},
			onConfirm: () => {
				// Validate the field
				const formulaValidation = formulaField.validate();

				// Check if the fields are valid
				if ( formulaValidation.isValid ) {
					// Get the values
					const calucaltionFormula = formulaField.getValue();

					// Clone the token data, don't modify the original
					const calculationToken = $.extend( true, {}, this );

					// Espace tokens characters from field values
					// Check if it's using tokens, and replace the reserved characters to prevent validation issues.
					const calculationFormulaEscaped = calucaltionFormula
						.replaceAll( '{', '«' )
						.replaceAll( '}', '»' )
						.replaceAll( ':', '¦' );

					// Change ID of the token
					calculationToken.id = calculationToken.id
						.replace( 'FORMULA', calculationFormulaEscaped );

					// Create the token name
					let tokenName = sprintf(
						UncannyAutomator.i18n.tokens.global.calculationTemplate,
						calculationFormulaEscaped
					);

					// Check if one of the fields have tokens
					if ( calucaltionFormula.includes( '{{' ) ) {
						// Try to parse tokens inside the token (beta, catch possible errors and add fallback)
						try {
							// Parse tokens
							const tokensInFields = new Tokens( 'action' );

							// Visually parse tokens in the "Post ID" and "Meta key" fields
							const calucaltionFormulaNode = tokensInFields.renderTextWithTokens( calucaltionFormula );

							// Create the token name
							tokenName = sprintf(
								UncannyAutomator.i18n.tokens.global.calculationTemplate,
								calucaltionFormulaNode.outerHTML
							);
						} catch ( e ) {
							// Output error if the developer mode is enabled
							if ( UncannyAutomator.dev.developerMode ) {
								// eslint-disable-next-line no-console
								console.warn( e );
							}
						}
					}

					// Change name of the token
					calculationToken.name = tokenName;

					// Destroy the modal
					modal.destroy();

					// Invoke the callback
					addTokenToField( calculationToken );
				}
			},
			afterHide: () => {
				modal.destroy();
			},
		} );
	}

	/**
	 * Generates the dynamic token name based on the data in the token ID
	 *
	 * @param {String} tokenID The token ID
	 * @return {String}         The token name, given the data in the ID
	 */
	getDynamicTokenName( tokenID ) {
		// Get the parts of the token
		// eslint-disable-next-line no-unused-vars, @wordpress/no-unused-vars-before-return
		const [ calculationBase, calcaultionFormula ] = tokenID.split( ':' );

		// Create the token name
		let tokenName = sprintf(
			UncannyAutomator.i18n.tokens.global.calculationTemplate,
			calcaultionFormula
		);

		// Check if one of the fields have tokens
		if ( calcaultionFormula.includes( '««' ) ) {
			// Try to parse tokens inside the token (beta, catch possible errors and add fallback)
			try {
				// Parse tokens
				const tokensInFields = new Tokens( 'action' );

				// Visually parse tokens in the "Post ID" and "Meta key" fields
				const calculationFormulaNode = tokensInFields.renderTextWithTokens(
						calcaultionFormula.replaceAll( '««', '{{' ).replaceAll( '»»', '}}' ).replaceAll( '¦', ':' )
					);

				// Create the token name
				tokenName = sprintf(
					UncannyAutomator.i18n.tokens.global.calculationTemplate,
					calculationFormulaNode.outerHTML
				);
			} catch ( e ) {
				// Output error if the developer mode is enabled
				if ( UncannyAutomator.dev.developerMode ) {
					// eslint-disable-next-line no-console
					console.warn( e );
				}
			}
		}

		return tokenName;
	}

	/**
	 * Returns true if the current token has the passed ID
	 *
	 * @param {String} tokenID The token ID
	 * @return {Boolean} True if the passed ID matches the ID of the current token
	 */
	is( tokenID ) {
		return tokenID.startsWith( 'CALCULATION:' );
	}

	/**
	 * Returns a variation of this token given a token ID with custom data
	 *
	 * @param {String} tokenID The token ID
	 * @return {PostMetaToken} The Post Meta token, with custom data
	 */
	getVariation( tokenID ) {
		// Create a new instance of this class
		const variation = new CalculationToken( {
			customID: tokenID,
			customName: this.getDynamicTokenName( tokenID ),
			context: this.context,
		} );

		return variation;
	}
}
